/*----------------------------------
- HOME SCREENER BLUEPRINT MOTION
----------------------------------*/

import React from 'react';

import Icon from '@/client/components/ui/icon/Icon';
import { HOME_SCREENER_BLUEPRINT } from '@/common/catalogs/landing/homeScreenerBlueprint';
import useHomeScreenerBlueprintVideo from './useHomeScreenerBlueprintVideo';
import './homeScreenerBlueprintMotion.css';

/*----------------------------------
- TYPES
----------------------------------*/

type HomeScreenerBlueprintMotionProps = {
    /**
     * Set where the figure sits right under a hero (`/product/search`): the film loads only
     * after the visitor's first scroll, pointer, key or touch, or a press on Play. On Home the
     * figure used to sit there with an eager poster and was the phone LCP element (5 to 6 s);
     * Home now shows the real row preview and the figure moved, so no consumer needs an eager
     * poster any more and every poster stays lazy.
     */
    deferUntilInteraction?: boolean;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const HomeScreenerBlueprintMotion = ({ deferUntilInteraction = false }: HomeScreenerBlueprintMotionProps) => {
    const {
        figureRef,
        isPlaying,
        mediaFailed,
        onCanPlay,
        onError,
        onPause,
        onPlay,
        showVideo,
        togglePlayback,
        variant,
        videoRef,
    } = useHomeScreenerBlueprintVideo({ deferUntilInteraction });
    const { desktop, mobile } = HOME_SCREENER_BLUEPRINT.variants;
    // The poster switches through `<picture>` and the video through the hook.
    // Both read the same breakpoint, but only the poster can afford to resolve
    // it in markup: it is the SSR paint, so it must be right before hydration.
    const media = HOME_SCREENER_BLUEPRINT.variants[variant];
    const captionId = `${HOME_SCREENER_BLUEPRINT.id}-caption`;
    const videoId = `${HOME_SCREENER_BLUEPRINT.id}-video`;
    const controlLabel = mediaFailed
        ? 'Animation unavailable'
        : isPlaying
          ? 'Pause animation'
          : 'Play animation';

    return (
        <figure
            ref={figureRef}
            class="ud-home-screener-blueprint"
            data-reveal
            data-testid="home-screener-blueprint-motion"
            aria-describedby={captionId}
        >
            <div class="ud-home-screener-blueprint-frame">
                <div
                    class="ud-home-screener-blueprint-media"
                    data-video-visible={showVideo ? 'true' : 'false'}
                >
                    {/* Art direction, not resolution switching: the portrait cut
                        is a different composition of the same film, so the
                        boundary is a media query and the geometry each side of
                        it is reserved in CSS. The portrait PNG is listed too, so
                        a phone without WebP support still gets portrait rather
                        than a landscape poster letterboxed into a 9:16 box. */}
                    <picture class="ud-home-screener-blueprint-poster">
                        <source
                            media={HOME_SCREENER_BLUEPRINT.mobileMediaQuery}
                            srcSet={mobile.posterWebpPath}
                            type="image/webp"
                        />
                        <source
                            media={HOME_SCREENER_BLUEPRINT.mobileMediaQuery}
                            srcSet={mobile.posterPngPath}
                            type="image/png"
                        />
                        <source srcSet={desktop.posterWebpPath} type="image/webp" />
                        <img
                            src={desktop.posterPngPath}
                            width={desktop.posterWidth}
                            height={desktop.posterHeight}
                            alt={HOME_SCREENER_BLUEPRINT.alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>
                    <video
                        ref={videoRef}
                        id={videoId}
                        class="ud-home-screener-blueprint-video"
                        width={media.videoWidth}
                        height={media.videoHeight}
                        muted
                        loop
                        playsInline
                        preload="none"
                        // No `poster` here: the `<picture>` above paints it and
                        // this element stays at opacity 0 until it is actually
                        // playing, so a poster attribute is never visible. It
                        // would still be fetched, and because SSR cannot know
                        // the viewport it would fetch the landscape frame on a
                        // phone, on top of the portrait one `<picture>` picked.
                        aria-hidden="true"
                        tabIndex={-1}
                        onCanPlay={onCanPlay}
                        onError={onError}
                        onPause={onPause}
                        onPlay={onPlay}
                    >
                        <source src={media.webmPath} type="video/webm" />
                        <source src={media.mp4Path} type="video/mp4" />
                    </video>
                    <button
                        type="button"
                        class="ud-home-screener-blueprint-control"
                        aria-controls={videoId}
                        aria-label={controlLabel}
                        disabled={mediaFailed}
                        onClick={togglePlayback}
                    >
                        {isPlaying ? <Icon name="pause" size={24} strokeWidth={2} /> : <Icon name="play" size={24} strokeWidth={2} />}
                        <span>{controlLabel}</span>
                    </button>
                </div>
            </div>
            <figcaption id={captionId} class="ud-home-screener-blueprint-caption">
                {HOME_SCREENER_BLUEPRINT.caption}
            </figcaption>
        </figure>
    );
};

export default HomeScreenerBlueprintMotion;
